import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Clock,
  Calendar,
  Eye,
  ArrowRight,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WebGL3DBackground from "@/components/WebGL3DBackground";
import type { BlogPost } from "@shared/schema";

export default function Blog() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { data: blogPosts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog-posts"],
  });

  // SEO
  useEffect(() => {
    document.title =
      "Blog - Latest Tech Insights & Software Development | VariableSoft";

    const metaDesc =
      document.querySelector('meta[name="description"]') ||
      document.createElement("meta");

    metaDesc.setAttribute("name", "description");
    metaDesc.setAttribute(
      "content",
      "Discover the latest insights in software development, AI, cloud computing, and enterprise solutions."
    );

    document.head.appendChild(metaDesc);
  }, []);

  const filteredPosts =
    blogPosts?.filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "all" ||
        post.category.toLowerCase() === selectedCategory.toLowerCase();

      return matchesSearch && matchesCategory && post.isPublished;
    }) || [];

  const categories = Array.from(
    new Set(blogPosts?.map((post) => post.category) || [])
  );

  /* -------------------- LOADING STATE -------------------- */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navigation />
        <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card
                  key={i}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border dark:border-gray-700"
                >
                  <Skeleton className="h-48 w-full rounded-t-2xl bg-gray-200 dark:bg-gray-700" />
                  <CardContent className="p-6 space-y-4">
                    <Skeleton className="h-4 w-24 bg-gray-200 dark:bg-gray-700" />
                    <Skeleton className="h-6 w-full bg-gray-200 dark:bg-gray-700" />
                    <Skeleton className="h-4 w-full bg-gray-200 dark:bg-gray-700" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  /* -------------------- MAIN UI -------------------- */
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <WebGL3DBackground />
      <Navigation />

      {/* HERO */}
      <section className="relative z-10 pt-32 pb-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Tech Insights & Articles
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Stay updated with the latest trends, insights, and innovations in
              software development, AI, and enterprise solutions.
            </p>
          </motion.div>

          {/* SEARCH + FILTER */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16 max-w-3xl mx-auto"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                <Input
                  placeholder="Search articles, authors, or topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger className="w-full md:w-56 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm text-gray-900 dark:text-white">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <SelectItem value="all" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                    All Categories
                  </SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat.toLowerCase()} className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>

          {/* FEATURED POST */}
          {filteredPosts.length > 0 && (
     <motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.2 }}
>
  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
    Featured Article
  </h2>

  <Card className="overflow-hidden rounded-3xl bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all">
    <div className="md:flex">
      <div className="md:w-1/2 relative overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <img
          src={filteredPosts[0].imageUrl}
          alt={filteredPosts[0].title}
          className="w-full h-72 md:h-96 object-contain"
        />
        <div className="absolute top-6 left-6">
          <Badge className="bg-blue-600 dark:bg-blue-700 text-white shadow-md">
            {filteredPosts[0].category}
          </Badge>
        </div>
      </div>

      <CardContent className="md:w-1/2 p-10 flex flex-col justify-center">
        <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {new Date(filteredPosts[0].publishedAt).toLocaleDateString()}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {filteredPosts[0].readTime} min read
          </span>
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {filteredPosts[0].views || 0} views
          </span>
        </div>

        <a
          href={`/blogs/${filteredPosts[0].slug}`}
          className="text-3xl font-bold text-gray-900 dark:text-white mb-4 hover:text-blue-600 dark:hover:text-blue-500 transition-colors"
        >
          {filteredPosts[0].title}
        </a>

        <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed text-lg">
          {filteredPosts[0].excerpt}
        </p>

        {/* Author + Button Layout (Mobile + Tablet stacked, Desktop inline) */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <img
              src={filteredPosts[0].authorAvatar}
              alt={filteredPosts[0].author}
              className="w-11 h-11 rounded-full object-cover ring-2 ring-blue-100 dark:ring-gray-700"
            />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {filteredPosts[0].author}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Author</p>
            </div>
          </div>

          <Button
            asChild
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-xl px-6 inline-flex items-center self-start lg:self-auto"
          >
            <a
              href={`/blogs/${filteredPosts[0].slug}`}
              className="inline-flex items-center whitespace-nowrap"
            >
              Read More
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </CardContent>
    </div>
  </Card>
</motion.div>

          )}

          {/* ALL ARTICLES are Here*/}
          {filteredPosts.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-20"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                Latest Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(1).map((post, index) => (
                  <Card 
                    key={post.id} 
                    className="overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all"
                  >
                    <div className="relative overflow-hidden bg-gray-50 dark:bg-gray-900">
                      <img
                        src={post.imageUrl}
                        alt={post.title}
                        className="w-full h-48 object-contain"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-blue-600 dark:bg-blue-700 text-white">
                          {post.category}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.publishedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {post.readTime} min
                        </span>
                      </div>
                      <a
                        href={`/blogs/${post.slug}`}
                        className="text-xl font-bold text-gray-900 dark:text-white mb-3 hover:text-blue-600 dark:hover:text-blue-500 transition-colors block"
                      >
                        {post.title}
                      </a>
                      <p className="text-gray-600 dark:text-gray-300 mb-6 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src={post.authorAvatar}
                            alt={post.author}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {post.author}
                          </span>
                        </div>
                      <Button
  variant="ghost"
  size="sm"
  className="
    text-blue-600 dark:text-blue-400
    hover:text-blue-700 dark:hover:text-blue-300
    hover:bg-blue-50 dark:hover:bg-white/10
    focus-visible:ring-2 focus-visible:ring-blue-500/50
  "
  asChild
>
  <a
    href={`/blogs/${post.slug}`}
    className="inline-flex items-center whitespace-nowrap"
  >
    Read
    <ArrowRight className="ml-2 h-4 w-4" />
  </a>
</Button>

                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* NO RESULTS */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <Search className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No articles found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search or filter to find what you're looking for.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
